
import React from "react";
import { format, differenceInDays, eachDayOfInterval } from "date-fns";
import { useTranslation } from "react-i18next";
import { Hotel, Flight } from "@/types/trip-types";
import { adjustHotelTimesBasedOnFlights } from "@/utils/hotelAdjustmentUtils";
import { Info, Plane, AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TripTimelineProps {
  flights: Flight[];
  hotels: Hotel[];
}

export const TripTimeline: React.FC<TripTimelineProps> = ({ flights, hotels }) => {
  const { t } = useTranslation();
  
  if (flights.length === 0 && hotels.length === 0) {
    return null;
  }

  // Adjust all hotels based on flights
  const adjustedHotels = hotels.map(hotel => adjustHotelTimesBasedOnFlights(hotel, flights));

  // Find the earliest and latest dates to determine timeline range
  let earliestDate: Date | null = null;
  let latestDate: Date | null = null;

  flights.forEach(flight => {
    const departureDate = flight.details?.departure?.time 
      ? new Date(flight.details.departure.time) 
      : new Date(`${format(flight.departureDate, "yyyy-MM-dd")}T${flight.departureTime}`);
    
    const arrivalDate = flight.details?.arrival?.time 
      ? new Date(flight.details.arrival.time) 
      : new Date(`${format(flight.arrivalDate, "yyyy-MM-dd")}T${flight.arrivalTime}`);

    if (!earliestDate || departureDate < earliestDate) {
      earliestDate = departureDate;
    }
    
    if (!latestDate || arrivalDate > latestDate) {
      latestDate = arrivalDate;
    }
  });

  hotels.forEach(hotel => {
    if (!earliestDate || hotel.checkIn < earliestDate) {
      earliestDate = hotel.checkIn;
    }
    
    if (!latestDate || hotel.checkOut > latestDate) {
      latestDate = hotel.checkOut;
    }
  });

  // If we don't have valid dates, return early
  if (!earliestDate || !latestDate) {
    return null;
  }

  // Ensure we have a whole day at each end
  earliestDate.setHours(0, 0, 0, 0);
  latestDate.setHours(23, 59, 59, 999);

  // Get all days in the range
  const days = eachDayOfInterval({ start: earliestDate, end: latestDate });
  const totalDays = days.length;
  
  // If too many days, we'll show a condensed view
  const isCondensedView = totalDays > 14;

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t("newTrip.timeline.title", "Trip Timeline")}</h3>
        {adjustedHotels.some(hotel => 
          hotel.adjustmentReason.checkIn || hotel.adjustmentReason.checkOut
        ) && (
          <div className="flex items-center text-xs text-amber-600">
            <AlertCircle className="w-3 h-3 mr-1" />
            <span>{t("newTrip.timeline.autoAdjusted", "Times auto-adjusted for optimal planning")}</span>
          </div>
        )}
      </div>

      <div className="relative border rounded-lg p-4 bg-gray-50">
        {/* Timeline header - dates */}
        <div className="flex border-b pb-2 mb-2">
          {isCondensedView ? (
            // Condensed view for long trips
            <>
              <div className="w-1/4 text-xs text-center">{format(days[0], "MMM d")}</div>
              <div className="w-2/4 text-xs text-center">...</div>
              <div className="w-1/4 text-xs text-center">{format(days[days.length - 1], "MMM d")}</div>
            </>
          ) : (
            // Full day-by-day view
            days.map((day, index) => (
              <div key={index} className="flex-1 text-xs text-center">
                {format(day, "MMM d")}
              </div>
            ))
          )}
        </div>

        {/* Flights timeline */}
        {flights.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <Plane className="w-4 h-4 mr-2 text-blue-500" />
              <span className="text-sm font-medium">{t("newTrip.timeline.flights", "Flights")}</span>
            </div>
            
            {flights.map((flight, index) => {
              const departureDate = flight.details?.departure?.time 
                ? new Date(flight.details.departure.time) 
                : new Date(`${format(flight.departureDate, "yyyy-MM-dd")}T${flight.departureTime}`);
              
              const arrivalDate = flight.details?.arrival?.time 
                ? new Date(flight.details.arrival.time) 
                : new Date(`${format(flight.arrivalDate, "yyyy-MM-dd")}T${flight.arrivalTime}`);
              
              // Calculate position and width for the flight
              const startDayIndex = differenceInDays(departureDate, earliestDate);
              const endDayIndex = differenceInDays(arrivalDate, earliestDate);
              
              // For condensed view, we use percentages
              const startPercent = isCondensedView 
                ? (startDayIndex / totalDays) * 100 
                : 0;
              
              const widthPercent = isCondensedView 
                ? ((endDayIndex - startDayIndex + 1) / totalDays) * 100 
                : 0;

              return (
                <div 
                  key={index} 
                  className="relative h-8 mb-2"
                >
                  {isCondensedView ? (
                    <div 
                      className="absolute h-full rounded-md bg-blue-100 border border-blue-300 flex items-center px-2"
                      style={{ 
                        left: `${startPercent}%`, 
                        width: `${widthPercent}%`,
                        minWidth: '100px'
                      }}
                    >
                      <span className="text-xs truncate">
                        {flight.flightNumber}: {format(departureDate, "HH:mm")} - {format(arrivalDate, "HH:mm")}
                      </span>
                    </div>
                  ) : (
                    <div className="flex h-full">
                      {days.map((day, dayIndex) => {
                        const isDepartureDay = format(day, "yyyy-MM-dd") === format(departureDate, "yyyy-MM-dd");
                        const isArrivalDay = format(day, "yyyy-MM-dd") === format(arrivalDate, "yyyy-MM-dd");
                        const isFlightDay = dayIndex >= startDayIndex && dayIndex <= endDayIndex;
                        
                        return (
                          <div key={dayIndex} className="flex-1 px-1">
                            {isFlightDay && (
                              <div 
                                className={`h-full rounded-md ${isDepartureDay && isArrivalDay 
                                  ? 'bg-blue-200 border border-blue-400' 
                                  : isDepartureDay 
                                    ? 'bg-blue-100 border-l border-t border-b border-blue-300 rounded-r-none' 
                                    : isArrivalDay 
                                      ? 'bg-blue-100 border-r border-t border-b border-blue-300 rounded-l-none' 
                                      : 'bg-blue-50 border-t border-b border-blue-200 rounded-none'
                                }`}
                              >
                                {(isDepartureDay || isArrivalDay) && (
                                  <div className="text-xs p-1 truncate">
                                    {isDepartureDay && (
                                      <span>{flight.flightNumber} {format(departureDate, "HH:mm")}</span>
                                    )}
                                    {isArrivalDay && !isDepartureDay && (
                                      <span>{format(arrivalDate, "HH:mm")}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Hotels timeline */}
        {hotels.length > 0 && (
          <div>
            <div className="flex items-center mb-2">
              <svg className="w-4 h-4 mr-2 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              <span className="text-sm font-medium">{t("newTrip.timeline.hotels", "Hotels")}</span>
            </div>
            
            {adjustedHotels.map((hotel, index) => {
              // Calculate position for original and adjusted dates
              const startDayIndex = differenceInDays(hotel.checkIn, earliestDate);
              const endDayIndex = differenceInDays(hotel.checkOut, earliestDate);
              
              const adjustedStartDayIndex = differenceInDays(hotel.adjustedCheckIn, earliestDate);
              const adjustedEndDayIndex = differenceInDays(hotel.adjustedCheckOut, earliestDate);
              
              // For condensed view, we use percentages
              const startPercent = isCondensedView 
                ? (startDayIndex / totalDays) * 100 
                : 0;
              
              const widthPercent = isCondensedView 
                ? ((endDayIndex - startDayIndex + 1) / totalDays) * 100 
                : 0;
              
              const hasAdjustments = hotel.adjustmentReason.checkIn || hotel.adjustmentReason.checkOut;

              return (
                <div 
                  key={index} 
                  className="relative h-8 mb-2"
                >
                  {isCondensedView ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div 
                            className={`absolute h-full rounded-md ${
                              hasAdjustments 
                                ? 'bg-green-100 border border-green-300' 
                                : 'bg-green-100 border border-green-300'
                            } flex items-center px-2`}
                            style={{ 
                              left: `${startPercent}%`, 
                              width: `${widthPercent}%`,
                              minWidth: '100px'
                            }}
                          >
                            <span className="text-xs truncate">
                              {hotel.name}: {format(hotel.checkIn, "MMM d")} - {format(hotel.checkOut, "MMM d")}
                              {hasAdjustments && (
                                <Info className="inline-block w-3 h-3 ml-1 text-amber-600" />
                              )}
                            </span>
                          </div>
                        </TooltipTrigger>
                        {hasAdjustments && (
                          <TooltipContent>
                            <div className="max-w-xs">
                              <p className="font-medium text-sm mb-1">{t("newTrip.timeline.adjustments", "Suggested Adjustments")}</p>
                              {hotel.adjustmentReason.checkIn && (
                                <p className="text-xs mb-1">
                                  {t("newTrip.timeline.checkIn", "Check-in")}: {format(hotel.adjustedCheckIn, "HH:mm")} 
                                  <span className="text-amber-600 ml-1">({hotel.adjustmentReason.checkIn})</span>
                                </p>
                              )}
                              {hotel.adjustmentReason.checkOut && (
                                <p className="text-xs">
                                  {t("newTrip.timeline.checkOut", "Check-out")}: {format(hotel.adjustedCheckOut, "HH:mm")} 
                                  <span className="text-amber-600 ml-1">({hotel.adjustmentReason.checkOut})</span>
                                </p>
                              )}
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <div className="flex h-full">
                      {days.map((day, dayIndex) => {
                        const isCheckInDay = format(day, "yyyy-MM-dd") === format(hotel.checkIn, "yyyy-MM-dd");
                        const isCheckOutDay = format(day, "yyyy-MM-dd") === format(hotel.checkOut, "yyyy-MM-dd");
                        const isStayDay = dayIndex >= startDayIndex && dayIndex < endDayIndex;
                        
                        const hasCheckInAdjustment = hotel.adjustmentReason.checkIn && isCheckInDay;
                        const hasCheckOutAdjustment = hotel.adjustmentReason.checkOut && isCheckOutDay;
                        
                        return (
                          <div key={dayIndex} className="flex-1 px-1">
                            {isStayDay && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div 
                                      className={`h-full rounded-md ${
                                        isCheckInDay 
                                          ? 'bg-green-100 border-l border-t border-b border-green-300 rounded-r-none' 
                                          : isCheckOutDay 
                                            ? 'bg-green-100 border-r border-t border-b border-green-300 rounded-l-none' 
                                            : 'bg-green-50 border-t border-b border-green-200 rounded-none'
                                      } ${
                                        (hasCheckInAdjustment || hasCheckOutAdjustment) ? 'border-amber-400' : ''
                                      }`}
                                    >
                                      {(isCheckInDay || isCheckOutDay) && (
                                        <div className="text-xs p-1 truncate flex items-center">
                                          <span>
                                            {isCheckInDay && hotel.name}
                                            {hasCheckInAdjustment && <Info className="inline-block w-3 h-3 ml-1 text-amber-600" />}
                                          </span>
                                          {isCheckOutDay && (
                                            <span>
                                              {hasCheckOutAdjustment && <Info className="inline-block w-3 h-3 mr-1 text-amber-600" />}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </TooltipTrigger>
                                  {(hasCheckInAdjustment || hasCheckOutAdjustment) && (
                                    <TooltipContent>
                                      <div className="max-w-xs">
                                        {hasCheckInAdjustment && (
                                          <p className="text-xs">
                                            {t("newTrip.timeline.checkInAdjusted", "Check-in adjusted")}: {format(hotel.adjustedCheckIn, "HH:mm")} 
                                            <span className="text-amber-600 block mt-1">{hotel.adjustmentReason.checkIn}</span>
                                          </p>
                                        )}
                                        {hasCheckOutAdjustment && (
                                          <p className="text-xs mt-1">
                                            {t("newTrip.timeline.checkOutAdjusted", "Check-out adjusted")}: {format(hotel.adjustedCheckOut, "HH:mm")} 
                                            <span className="text-amber-600 block mt-1">{hotel.adjustmentReason.checkOut}</span>
                                          </p>
                                        )}
                                      </div>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
